import React, { useMemo, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import Tabs from 'antd/es/tabs';
import message from 'antd/es/message';

import userRole from 'config/userRole';
import useAuth from 'hooks/useAuth';
import { useFeatureToggleItems } from 'components/FeatureToggle';

import MainLayout from 'layouts/Main.layout';
import { Row, Col } from 'components/layout';
import PageHeader from 'components/MainPageHeader';

import featureToggles from 'config/featureToggles';

import ProfileTab from '../tabs/Profile.tab';
import TargetTimeTab from '../tabs/TargetTime.tab';
import PayForPerformanceTab from '../tabs/PayForPerformance.tab';
import PayForPerformanceTiersTab from '../tabs/PayForPerformanceTiers.tab';
import WorkingHoursTab from '../tabs/WorkingHours.tab';
import AlertPreferences from '../tabs/AlertsPrefereneces.tab';
import LocationTab from '../tabs/Location.tab';

const settingsTabs = [
  {
    key: 'changePass',
    title: 'Change Password',
    component: ProfileTab,
  },
  {
    key: 'alerts',
    title: 'Alerts',
    component: AlertPreferences,
  },
  {
    key: 'targetTime',
    title: 'Target Time',
    component: TargetTimeTab,
    adminOnly: true,
  },
  {
    key: 'p4p',
    title: 'Pay for Performance',
    component: PayForPerformanceTab,
    adminOnly: true,
  },
  {
    key: 'p4pTiers',
    title: 'Pay for Performance Tiers',
    component: PayForPerformanceTiersTab,
    adminOnly: true,
  },
  {
    key: 'workingHours',
    title: 'Working Hours',
    component: WorkingHoursTab,
    adminOnly: true,
  },
  {
    key: 'location',
    title: 'Location',
    component: LocationTab,
    adminOnly: true,
  },
];

const SettingsPage = (props) => {
  const history = useHistory();
  const auth = useAuth();
  const settingsFeatureToggles = useFeatureToggleItems(featureToggles.SETTINGS);

  const tabs = useMemo(() => {
    return settingsTabs
      // filter admin only routes
      .filter(tab => {
        return auth.state.currentUser.role === userRole.ADMIN || !tab.adminOnly;
      })
      // filter disabled settings by feature toggles
      .filter(tab => {
        const featureToggleItem = settingsFeatureToggles.find(item => item.itemName === tab.key);
        if (featureToggleItem && featureToggleItem.visible === false) {
          return false;
        }
        return true;
      });
  }, [auth.state, settingsFeatureToggles]);

  const defaultActiveTab = useMemo(() => {
    const activeKey = props.location.hash.substr(1);
    return activeKey || tabs[0]?.key;
  }, [props.location, tabs]);

  const onTabChange = useCallback((key) => {
    history.replace(`#${key}`);
  }, [history]);

  const showMessage = useCallback((level, content, done = null) => {
    if (!content) return;
    
    let config = {
      duration: 2,
      style: {
        marginLeft: '250px',
      },
    };

    if (typeof content === 'string') {
      config = {
        ...config,
        content,
      };
    } else {
      config = {
        ...content,
      };
    }

    message[level](config).then(() => done && done());
  }, []);

  return (
    <MainLayout>
      <PageHeader
        title="Settings"
        icon="settings"
      />
      <Row style={{ padding: '24px 24px 0 24px', height: 'calc(100% - 84px)' }}>
        <Col span={24}>
          <SettingsTabs defaultActiveKey={defaultActiveTab} onChange={onTabChange}>
            {tabs.map(({ key, title, component: Component }) => (
              <Tabs.TabPane key={key} tab={title}>
                <Component
                  showMessage={showMessage}
                />
              </Tabs.TabPane>
            ))}
          </SettingsTabs>
        </Col>
      </Row>
    </MainLayout>
  );
};

const SettingsTabs = styled(Tabs)`
  background-color: #fff;
  height: 100%;

  & .ant-tabs-tab {
    padding: 16px;
    margin: 0 16px 0 0;
    font-size: 15px;
  }

  & .ant-tabs-nav {
    margin-bottom: 24px;
  }

  & .ant-tabs-content-holder {
    padding: 16px;
  }
`;

export default SettingsPage;